import {
    AddressActions,
    AddressState,
    BasisActions,
    BasisState,
    CalendarActions,
    CalendarState,
    GeneralActions,
} from '@raskrask-dk/bookingflow-state-machine'

import { Goal } from '@raskrask-dk/bookingflow-state-machine'
import { RRDate } from '@raskrask-dk/raskrask-utils'
import { useEffect } from 'react'
import { BfsmRepoAdaptors } from '../../adaptors/bfsmRepoAdaptors'
import { useBFSM } from '../../contexts/bfsmContext'
import { BasisTypeBF, GoalBF } from '../../models'
import { useAppDispatch } from '../../redux/hooks'
import { setErrorcase, setIsLoading } from '../../redux/reducers/appSettings'
import { useBFSMValue } from '../../services/hooks/useBFSMValues'
import { sortGoals } from './sortGoals'

export const useLoadCalendar = (
    setGoals: React.Dispatch<React.SetStateAction<GoalBF[]>>,
    setGoal: (goal: Goal) => void,
    numberOfDaysLoaded: number,
    loadingStartDate: RRDate,
    flow: string | undefined,
    partners: number[],
    rebookingPartnerId?: number
): void => {
    const [BFSM] = useBFSM()
    const dispatch = useAppDispatch()
    const [address] = useBFSMValue<
        AddressState,
        AddressActions.Next,
        'address'
    >('address')
    const [basisType] = useBFSMValue<
        BasisState,
        BasisActions.SetBasisType,
        'basisType'
    >('basisType')
    const [customerType] = useBFSMValue<
        BasisState,
        BasisActions.SetCustomerType,
        'customerType'
    >('customerType')
    const [goal] = useBFSMValue<CalendarState, CalendarActions.SetGoal, 'goal'>(
        'goal'
    )
    const [service, setService] = useBFSMValue<
        CalendarState,
        CalendarActions.SetServiceType,
        'serviceType'
    >('serviceType', CalendarActions.SetServiceType)

    useEffect(() => {
        //Temp IFS
        if (BFSM.currentState().type === 'CALENDAR') {
            const getData = async () => {
                try {
                    dispatch(setIsLoading(true))

                    const loadinDateEnd = new RRDate(
                        loadingStartDate.toString('yyyy-MM-dd'),
                        'yyyy-MM-dd'
                    ).addDays(numberOfDaysLoaded + 1)

                    const bookableSchedulesObj =
                        BfsmRepoAdaptors.toBookableSchedulesDTO(
                            address!,
                            basisType as BasisTypeBF,
                            customerType!,
                            loadingStartDate,
                            loadinDateEnd
                        )

                    if (flow === 'rebook' && rebookingPartnerId) {
                        bookableSchedulesObj.filters = {
                            profileIds: [rebookingPartnerId],
                        }
                    }

                    const bfModels = await BFSM.service.loadSchedules(
                        bookableSchedulesObj
                    )

                    const goalsBF = sortGoals(bfModels.goals as GoalBF[])
                    setGoals(goalsBF)

                    const presetGoal = goalsBF[0]
                    if (!goal && !service) {
                        setGoal(presetGoal)
                    } else if (goal) {
                        const newGoal =
                            goalsBF.find((g) => g.id === goal.id) ?? goal
                        setGoal(newGoal)
                    }

                    if (bfModels.partners.length === 0) {
                        dispatch(setErrorcase('NoPartnersAvailable'))
                        BFSM.doAction(GeneralActions.GoToErrorState, undefined)
                    }

                    dispatch(setIsLoading(false))
                } catch (err: any) {
                    console.error(err)
                    dispatch(setIsLoading(false))
                    if (err.message === 'No availabilities for Partner(s)') {
                        dispatch(setErrorcase('LoadCalendarError'))
                        BFSM.doAction(GeneralActions.GoToErrorState, undefined)
                    } else {
                        dispatch(setErrorcase('NoPartnersAvailable'))
                        BFSM.doAction(
                            GeneralActions.ChooseNoAvailable,
                            undefined
                        )
                    }
                }
            }
            getData()
        }
    }, [flow])
}
